// Free, on-device categorizer. Order of trust:
//   1. what the person already did with the same merchant (their own history)
//   2. a category column in the file itself
//   3. a built-in merchant/keyword dictionary
//   4. "Other" — flagged as unsure, which is the only thing AI is ever asked about
import { merchantKey } from "./smartImport.js";

// Specific patterns first — first match wins.
const RULES = [
  ["Dining", /uber\s?eats|doordash|grubhub|deliveroo|just\s?eat|zomato|swiggy|postmates|seamless|menulog|foodpanda/],
  ["Marketing", /facebook\s?ads|fb\s?ads|meta\s?ads|google\s?ads|adwords|linkedin\s?ads|tiktok\s?ads|mailchimp|constant\s?contact|vistaprint|hootsuite|buffer\.com|advertis|sendinblue|klaviyo/],
  ["Software", /amazon web services|\baws\b|digitalocean|heroku|vercel|netlify|cloudflare|godaddy|namecheap|squarespace|wix\.com|shopify|atlassian|jira|github|gitlab|slack|zoom\.us|\bzoom\b|dropbox|notion|figma|canva|openai|chatgpt|anthropic|claude\.ai|google\s?(?:workspace|gsuite|cloud|storage|one)|g suite|microsoft|office\s?365|adobe|1password|lastpass|icloud|apple\.com\/bill|quickbooks|intuit|xero|freshbooks|zapier|twilio|sendgrid|linode|airtable|asana|monday\.com|trello|loom|calendly|docusign|bubble\.io|webflow|jetbrains|sentry|datadog|posthog|supabase/],
  ["Travel", /airlines?|airways|\bdelta\b|united air|american air|southwest|jetblue|spirit air|ryanair|easyjet|lufthansa|emirates|qatar air|indigo|air india|vistara|expedia|booking\.com|airbnb|vrbo|hotel|marriott|hilton|hyatt|\bmotel\b|holiday inn|\binn\b|hertz|avis|enterprise rent|budget rent|amtrak|greyhound|flixbus|kayak|travelocity|agoda|makemytrip|cleartrip|irctc|airport|tsa precheck/],
  ["Transport", /\buber\b|\blyft\b|taxi|\bcab\b|\bola\b|metro|transit|railway|\brail\b|\bbus\b|parking|\btoll|e-?zpass|fastag|\bshell\b|chevron|exxon|\bbp\b|mobil|texaco|citgo|valero|marathon|sunoco|\bwawa\b|circle k|gas station|\bfuel\b|petrol|autozone|jiffy lube|car wash|o'?reilly auto|advance auto|napa auto|nola ?(?:ready|rta)|rta |tesla supercharger|chargepoint|electrify america|dmv|vehicle registration/],
  ["Groceries", /kroger|safeway|publix|aldi|trader joe|whole foods|tesco|sainsbury|asda|lidl|morrisons|waitrose|woolworths|\bcoles\b|bigbasket|blinkit|zepto|dmart|reliance fresh|grocer|supermarket|h-?e-?b\b|wegmans|food lion|winn-?dixie|harris teeter|albertsons|sprouts|instacart|fresh market|carrefour|\bspar\b|rouses|breaux mart|matherne|giant eagle|meijer|piggly|market basket|fresh direct|freshdirect|farmers market|butcher/],
  ["Dining", /restaurant|\bcafe\b|caf\u00e9|coffee|starbucks|mcdonald|burger|\bkfc\b|subway|domino|pizza|taco|chipotle|dunkin|chick-?fil-?a|wendy'?s|popeyes|panera|bar & grill|bar and grill|diner|bakery|bistro|sushi|\bgrill\b|\bpub\b|brewery|tavern|\bbar\b|steakhouse|noodle|ramen|bbq|barbecue|donut|doughnut|ice cream|gelato|smoothie|juice bar|five guys|shake shack|in-?n-?out|sonic drive|jimmy john|jersey mike|raising cane|whataburger|waffle house|cracker barrel|ihop|denny'?s|applebee|olive garden|buffalo wild|wingstop|panda express|cafe du monde|po-?boy/],
  ["Housing", /\brent\b|mortgage|\bhoa\b|property tax|landlord|property man|realty|apartments?\b|\blease\b|home owners|homeowners|rental payment|renters/],
  ["Utilities", /electric|entergy|\bpower\b|water bill|water dept|sewer|sewerage|internet|comcast|xfinity|spectrum|\bcox\b|at&t|\batt\b|verizon|t-?mobile|vodafone|airtel|\bjio\b|bsnl|wireless|utility|utilities|\btrash\b|waste management|\bwaste\b|virgin media|\bsky\b|optimum|centurylink|frontier|broadband|\bgas bill|natural gas|bt group|mint mobile|cricket wireless|google fi|visible wireless/],
  ["Insurance", /insurance|geico|state farm|progressive|allstate|liberty mutual|\blic\b|usaa|farmers ins|nationwide|aflac/],
  ["Health", /pharmacy|\bcvs\b|walgreens|rite aid|doctor|\bdr\.? |clinic|hospital|dental|dentist|medical|health|urgent care|optical|vision|labcorp|quest diag|therapy|therapist|apollo|practo|\b1mg\b|pharmeasy|planet fitness|anytime fitness|equinox|\bymca\b|gym|fitness|orangetheory|crossfit|massage|chiropract|dermat|pediatric|optometr|ochsner|tulane med|lcmc/],
  ["Entertainment", /netflix|spotify|hulu|disney\s?\+|disney ?plus|\bhbo\b|max\.com|youtube\s?(?:premium|tv)|apple music|prime video|paramount|peacock|cinema|\bamc\b|regal|theatre|theater|movie|ticketmaster|stubhub|\bsteam\b|playstation|xbox|nintendo|twitch|audible|concert|bowling|museum|zoo|aquarium|eventbrite|livenation|live nation|fandango|topgolf|escape room|arcade|patreon|substack/],
  ["Education", /tuition|university|college|\bschool\b|coursera|udemy|udacity|skillshare|masterclass|textbook|bookstore|pluralsight|duolingo/],
  ["Fees", /bank fee|service charge|overdraft|atm fee|monthly maintenance|maintenance fee|interest charge|finance charge|late fee|foreign transaction|wire fee|annual fee|stripe fee|paypal fee|\bnsf\b|returned item/],
  ["Gifts & Donations", /donation|charity|gofundme|\bchurch\b|red cross|unicef|salvation army|go fund me|\btithe/],
  ["Office Supplies", /staples|office depot|officemax|stationery|\bink\b|\busps\b|ups store|fedex|uline|\bpostage|stamps\.com|shipstation|pitney/],
  ["Contractors", /upwork|fiverr|toptal|freelancer|contractor|consulting|payroll|gusto|\badp\b|paychex|justworks|deel\.com|remote\.com|bookkeep|accountant|\bcpa\b|attorney|law office|legal ?zoom/],
  ["Shopping", /amazon|amzn|\btarget\b|walmart|wal-?mart|costco|sam'?s club|ebay|etsy|best buy|ikea|home depot|lowe'?s|macy|nordstrom|h&m|zara|uniqlo|\bnike\b|adidas|flipkart|myntra|aliexpress|shein|temu|tj ?maxx|marshalls|\bross\b|kohl|old navy|\bgap\b|dollar tree|dollar general|five below|wayfair|apple store|apple\.com|bed bath|michaels|hobby lobby|petco|petsmart|chewy|sephora|ulta|lululemon|burlington|big lots|ace hardware|harbor freight/],
];

// Rules for category *names* that come from the bank file ("Food & Drink", "Gas", …)
const FILE_CATEGORY_RULES = [
  ["Dining", /food|drink|restaurant|dining|cafe|coffee|takeaway|eating out/],
  ["Groceries", /grocer|supermarket/],
  ["Transport", /gas|fuel|auto|transport|car|taxi|commut|parking|vehicle/],
  ["Housing", /home|housing|rent|mortgage|household|mortgage/],
  ["Utilities", /utilit|bills|phone|internet|telecom|mobile/],
  ["Health", /health|medical|wellness|pharm|fitness|doctor|dental/],
  ["Entertainment", /entertain|leisure|fun|movies|music|games|hobbies|recreation/],
  ["Travel", /travel|hotel|flight|holiday|vacation|lodging/],
  ["Shopping", /shop|retail|clothing|general merch|personal|merchandise/],
  ["Software", /software|subscription|saas|cloud|hosting|web/],
  ["Office Supplies", /office|supplies|stationery|postage|shipping/],
  ["Marketing", /marketing|advertis|promotion/],
  ["Contractors", /contract|professional|services|consult|freelanc|payroll|wages|labou?r/],
  ["Insurance", /insur/],
  ["Education", /educat|school|tuition|learning|course/],
  ["Fees", /fee|charge|bank|interest/],
  ["Gifts & Donations", /gift|donat|charit/],
];

// Names a profile might already use for each canonical category, best first.
const ALIASES = {
  Groceries: ["Groceries", "Food", "Supermarket"],
  Dining: ["Dining", "Restaurants", "Eating Out", "Food & Drink", "Meals & Entertainment", "Meals", "Food"],
  Transport: ["Transport", "Transportation", "Car", "Auto", "Vehicle", "Fuel", "Travel"],
  Housing: ["Housing", "Rent", "Mortgage", "Home", "Rent & Utilities", "Occupancy"],
  Utilities: ["Utilities", "Bills", "Bills & Utilities", "Phone & Internet", "Telecom"],
  Shopping: ["Shopping", "Office Supplies", "Supplies", "Retail", "Merchandise", "Personal"],
  Health: ["Health", "Medical", "Healthcare", "Wellness", "Fitness"],
  Entertainment: ["Entertainment", "Leisure", "Fun", "Meals & Entertainment"],
  Software: ["Software", "Subscriptions", "Software & Subscriptions", "SaaS", "Tools", "Entertainment"],
  Travel: ["Travel", "Transport", "Transportation", "Holiday", "Vacation"],
  "Office Supplies": ["Office Supplies", "Supplies", "Office", "Shopping"],
  Marketing: ["Marketing", "Advertising", "Promotion"],
  Contractors: ["Contractors", "Contract Labor", "Freelancers", "Professional Services", "Payroll", "Services"],
  Insurance: ["Insurance"],
  Education: ["Education", "Learning", "Training", "Courses"],
  Fees: ["Fees", "Bank Fees", "Bank Charges", "Fees & Charges"],
  "Gifts & Donations": ["Gifts & Donations", "Gifts", "Donations", "Charity"],
};

const norm = (s) => String(s || "").toLowerCase().replace(/\s+/g, " ").trim();

export function resolveCategory(canonical, profileCategories) {
  const cats = profileCategories || [];
  const byLower = new Map(cats.map((c) => [norm(c), c]));
  for (const alias of ALIASES[canonical] || [canonical]) {
    if (byLower.has(norm(alias))) return { name: byLower.get(norm(alias)), isNew: false };
  }
  // loose: the profile's own wording contains the word ("Food & Dining" ⊃ "dining")
  const word = norm(canonical).split(" ")[0];
  const loose = cats.find((c) => norm(c).includes(word) && word.length > 3);
  if (loose) return { name: loose, isNew: false };
  return { name: canonical, isNew: true };
}

function canonicalFromRules(text) {
  const t = norm(text);
  if (!t) return null;
  for (const [cat, re] of RULES) if (re.test(t)) return cat;
  return null;
}

function canonicalFromFileCategory(text) {
  const t = norm(text);
  if (!t) return null;
  for (const [cat, re] of FILE_CATEGORY_RULES) if (re.test(t)) return cat;
  return null;
}

// What the person has already decided for a merchant, from their own entries.
export function buildHistory(expenses, categories) {
  const valid = new Set((categories || []).map(norm));
  const counts = {};
  (expenses || []).forEach((e) => {
    if (!e.note || !valid.has(norm(e.category))) return;
    const k = merchantKey(e.note);
    if (!k) return;
    counts[k] = counts[k] || {};
    counts[k][e.category] = (counts[k][e.category] || 0) + 1;
  });
  const out = {};
  Object.entries(counts).forEach(([k, byCat]) => {
    out[k] = Object.entries(byCat).sort((a, b) => b[1] - a[1])[0][0];
  });
  return out;
}

// Assigns a category to every expense row. Returns rows with
// { category, confidence: "history"|"file"|"rule"|"unsure", isNewCategory }
export function categorizeRows(rows, { categories, history = {} }) {
  const cats = categories || [];
  const catSet = new Map(cats.map((c) => [norm(c), c]));
  const other = catSet.get("other") || "Other";

  const tallyNew = {};
  const assigned = rows.map((r) => {
    const key = merchantKey(r.raw || r.merchant);
    if (key && history[key]) return { ...r, category: history[key], confidence: "history", _new: false };

    if (r.fileCategory) {
      const exact = catSet.get(norm(r.fileCategory));
      if (exact) return { ...r, category: exact, confidence: "file", _new: false };
      const canon = canonicalFromFileCategory(r.fileCategory);
      if (canon) {
        const res = resolveCategory(canon, cats);
        if (!res.isNew) return { ...r, category: res.name, confidence: "file", _new: false };
      }
    }

    const canon = canonicalFromRules(`${r.raw} ${r.matchText || ""}`);
    if (canon) {
      const res = resolveCategory(canon, cats);
      if (!res.isNew) return { ...r, category: res.name, confidence: "rule", _new: false };
      tallyNew[canon] = (tallyNew[canon] || 0) + 1;
      return { ...r, category: canon, confidence: "rule", _new: true, _canon: canon };
    }

    if (r.fileCategory) {
      // Their file had a category we couldn't map — keep their wording rather than lose it
      const nice = r.fileCategory.length > 28 ? r.fileCategory.slice(0, 28) : r.fileCategory;
      tallyNew[nice] = (tallyNew[nice] || 0) + 1;
      return { ...r, category: nice, confidence: "file", _new: true, _canon: nice };
    }
    return { ...r, category: other, confidence: "unsure", _new: false };
  });

  // Don't clutter someone's list with a brand-new category for a single stray row
  const finalRows = assigned.map((r) => {
    if (r._new && (tallyNew[r._canon] || 0) < 2) return { ...r, category: other, confidence: r.confidence === "file" ? "file" : "unsure", _new: false };
    return r;
  });

  const existing = new Set(cats.map(norm));
  const newCategories = [];
  finalRows.forEach((r) => {
    if (r.category && !existing.has(norm(r.category)) && !newCategories.some((c) => norm(c) === norm(r.category))) newCategories.push(r.category);
  });
  return { rows: finalRows.map(({ _new, _canon, ...r }) => r), newCategories };
}

// Returns an {id:count} style summary for UI
export function confidenceCounts(rows) {
  const out = { history: 0, file: 0, rule: 0, unsure: 0, ai: 0 };
  rows.forEach((r) => { if (r.kind === "expense") out[r.confidence] = (out[r.confidence] || 0) + 1; });
  return out;
}
